// Каналы входа генерации описаны одним местом: порядок, заголовок и то, как
// канал достаётся из снимка запроса. Этим описанием живут оба экрана — ряд
// входов в баре и блок входов в сообщении, — поэтому новый канал (видео-референс,
// голос) добавляется одной записью, а не правкой двух мест, которые разойдутся.
//
// Каналы читаются ИЗ СНИМКА `requestPayload`, а не из текущей библиотеки:
// элемент могли переименовать или удалить, а сообщение обязано показывать то, с
// чем генерация реально ушла.

import type { TFunction } from "i18next";

import { isImageInputRole } from "~/shared/data/chat-models";

import type { ArtifactType, ImageInputRole, ModeConfig } from "~/shared/data/chat-models";

import type { ChatAttachment, LegacyGenerationJob } from "../types";

export type GenerationInputChannel = "frames" | "elements" | "references" | "video" | "audio";

/** Порядок каналов — один и тот же в баре и в истории. */
export const GENERATION_INPUT_CHANNELS: readonly GenerationInputChannel[] = [
    "frames",
    "elements",
    "references",
    "video",
    "audio",
];

/**
 * Заголовок канала (namespace `chat`) — общий для колонки бара и блока в сообщении.
 *
 * Канал `frames` несёт `inputImageUrls`, но означает разное в зависимости от того,
 * что модель порождает: у видео это кадр (первый / последний), у изображения —
 * исходная картинка, которую правят. Подписывать правку «Кадрами» неверно, поэтому
 * заголовок выбирается по типу артефакта. Тип не известен (историческая генерация,
 * модель отключили) — остаётся прежний нейтральный заголовок.
 */
export function inputChannelTitleKey(
    channel: GenerationInputChannel,
    artifactType?: ArtifactType | null,
): string {
    if (channel === "frames" && artifactType === "image") return "prompt.inputColumns.sourceImages";
    return `prompt.inputColumns.${channel}`;
}

export interface MessageInputItem {
    /** Что показываем плиткой; `null` — картинки в снимке нет (элемент без картинок). */
    url: string | null;
    /**
     * Чем показывать плитку и чем открывать: картинка, постер видео или звук
     * плеером. Без этого поля видео и звук рисовались бы `<img>` на битую ссылку.
     */
    kind?: "image" | "video" | "audio";
    /** Подпись под плиткой: роль кадра или имя элемента. Пусто — подписи нет. */
    label: string;
    /** Пояснение по наведению: описание элемента, полное имя. */
    title?: string;
    /** Лента лайтбокса по клику: у элемента — все его картинки, у кадра — он сам. */
    lightbox: readonly string[];
    /**
     * Снимок элемента, если у входа есть карточка (у кадров и референсов её нет).
     * Поля — те, что сохранены с запросом: имя, описание, картинки. Тип, источник
     * и даты пришлось бы брать из текущей библиотеки, а тогда история перестанет
     * показывать то, с чем генерация реально ушла.
     */
    snapshot?: { name: string; description: string; imageUrls: readonly string[] };
}

export interface MessageInputChannelView {
    channel: GenerationInputChannel;
    items: MessageInputItem[];
}

export interface CollectMessageInputsParams {
    t: TFunction;
    attachments: readonly ChatAttachment[];
    payload: LegacyGenerationJob["requestPayload"];
    /** Режим генерации на момент запроса; `null` — режим не резолвится, роли кадров неизвестны. */
    mode: ModeConfig | null;
}

const asStringArray = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === "string") : [];

/**
 * Роль кадра восстанавливается позиционно: контракт генерации —
 * `inputImageUrls[i]` ↔ `imageRoles[i]`. Роли, которых фронт не знает (бэк завёл
 * новую), пропускаем: кадр покажется без подписи, а не с чужой.
 *
 * Первым аргументом идёт сырое поле снимка (`unknown`): у исторических генераций
 * его может не быть вовсе, и разбирать его на каждой стороне отдельно — лишний
 * повод разойтись.
 */
export function buildRoleByS3Url(
    payloadImageUrls: unknown,
    roles: readonly ImageInputRole[],
): Map<string, ImageInputRole> {
    const byUrl = new Map<string, ImageInputRole>();
    asStringArray(payloadImageUrls).forEach((url, index) => {
        const role = roles[index];
        if (role && isImageInputRole(role)) byUrl.set(url, role);
    });
    return byUrl;
}

/**
 * Кадры генерации: картинки, которые снимок назвал кадрами (`inputImageUrls`),
 * с ролевыми подписями по позиции.
 *
 * Канал набирается ИЗ СНИМКА, а не из всех вложений-картинок сообщения: вложением
 * приходит каждая входная картинка, включая референсы Omni, и «все картинки =
 * кадры» рисовало референс безымянной плиткой в «Кадрах». Снимок без обоих полей
 * (историческая генерация до канала референсов) — единственный случай, когда
 * канал по-прежнему берёт все картинки: иначе такая генерация лишилась бы входов
 * вовсе.
 */
function framesChannel({ t, attachments, payload, mode }: CollectMessageInputsParams): MessageInputItem[] {
    const frameUrls = asStringArray(payload.inputImageUrls);
    const snapshotKnowsChannels =
        frameUrls.length > 0 || asStringArray(payload.referenceImageUrls).length > 0;
    const roleByUrl = buildRoleByS3Url(payload.inputImageUrls, mode?.imageRoles ?? []);

    return attachments
        .filter((attachment) => attachment.kind === "image")
        .filter((attachment) => !snapshotKnowsChannels || frameUrls.includes(attachment.s3Url))
        .map((attachment) => {
            const role = roleByUrl.get(attachment.s3Url);
            const label = role ? t(`prompt.imageRoles.${role}`) : "";
            return { url: attachment.s3Url, label, lightbox: [attachment.s3Url] };
        });
}

function elementsChannel({ payload }: CollectMessageInputsParams): MessageInputItem[] {
    return (payload.referenceElements ?? []).map((element) => ({
        url: element.imageUrls[0] ?? null,
        label: element.name,
        title: element.description || element.name,
        lightbox: element.imageUrls,
        snapshot: {
            name: element.name,
            description: element.description,
            imageUrls: element.imageUrls,
        },
    }));
}

/**
 * Референсные картинки — собственный канал эталона (Kling Omni: `refer_image`),
 * не кадры: референс описывает, что должно быть в кадре, и упоминается в промпте,
 * поэтому ролевых подписей у него не бывает. Порядок — из снимка, а не из порядка
 * вложений: снимок хранит то, с чем генерация ушла.
 */
function referencesChannel({ payload }: CollectMessageInputsParams): MessageInputItem[] {
    return asStringArray(payload.referenceImageUrls).map((url) => ({
        url,
        label: "",
        lightbox: [url],
    }));
}

/**
 * Входное видео и входной звук — собственные каналы, а не «прочие файлы».
 *
 * Снимок различал их с самого начала (`inputVideoUrls` / `inputAudioUrls`), но до
 * человека это не доезжало: оба падали в общий блок файлов без имени канала. Для
 * моделей, которые принимают и то и другое (Seedance omni — по три тех и других),
 * это и есть «поддержано в коде, но не для человека».
 */
function videoChannel({ payload }: CollectMessageInputsParams): MessageInputItem[] {
    return asStringArray(payload.inputVideoUrls).map((url) => ({
        url,
        kind: "video" as const,
        label: "",
        lightbox: [url],
    }));
}

function audioChannel({ payload }: CollectMessageInputsParams): MessageInputItem[] {
    return asStringArray(payload.inputAudioUrls).map((url) => ({
        url,
        kind: "audio" as const,
        label: "",
        lightbox: [url],
    }));
}

const CHANNEL_EXTRACTORS: Record<
    GenerationInputChannel,
    (params: CollectMessageInputsParams) => MessageInputItem[]
> = {
    frames: framesChannel,
    elements: elementsChannel,
    references: referencesChannel,
    video: videoChannel,
    audio: audioChannel,
};

/**
 * Каналы входа сообщения по порядку. Пустые каналы не возвращаются: в баре
 * пустой слот приглашает заполнить, а в истории пустому месту взяться неоткуда.
 */
export function collectMessageInputChannels(params: CollectMessageInputsParams): MessageInputChannelView[] {
    return GENERATION_INPUT_CHANNELS.map((channel) => ({
        channel,
        items: CHANNEL_EXTRACTORS[channel](params),
    })).filter((view) => view.items.length > 0);
}

/**
 * Вложения, не попавшие ни в один канал, — их показывает общий блок файлов.
 *
 * Видео и звук вычитаются, только если снимок назвал их своим каналом: у
 * исторической генерации таких полей нет вовсе, и без этой оговорки её вход
 * пропал бы с экрана совсем — канал пуст, а из общего блока файл уже вычтен.
 */
export function nonChannelAttachments(
    attachments: readonly ChatAttachment[],
    payload?: LegacyGenerationJob["requestPayload"],
): ChatAttachment[] {
    const shownByChannel = new Set([
        ...asStringArray(payload?.inputVideoUrls),
        ...asStringArray(payload?.inputAudioUrls),
    ]);
    return attachments.filter(
        (attachment) => attachment.kind !== "image" && !shownByChannel.has(attachment.s3Url),
    );
}
